import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { RateCardInput } from "@reina/shared";
import type { RateCard, RateCardKind, Supplier } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  SupplierAvatar,
  TableWrap,
  useToast,
} from "../../components";
import type { PillTone } from "../../components";
import {
  useCreateRateCard,
  useRateCards,
  useSuppliers,
  useUpdateRateCard,
} from "../../api/catalogue";
import { errorMessage } from "../../lib/format";

const KIND_LABEL: Record<RateCardKind, { label: string; tone: PillTone }> = {
  main: { label: "Main", tone: "ok" },
  customer_developer: { label: "Developer", tone: "warn" },
  supplier_brand: { label: "Brand", tone: "neutral" },
};

export function RateCardsPage() {
  const [supplierFilter, setSupplierFilter] = useState("");
  const suppliers = useSuppliers();
  const rateCards = useRateCards(
    supplierFilter ? { limit: 500, supplierId: supplierFilter } : { limit: 500 }
  );
  const items = rateCards.data?.items ?? [];

  const supplierById = useMemo(() => {
    const map = new Map<string, Supplier>();
    for (const supplier of suppliers.data?.items ?? []) map.set(supplier.id, supplier);
    return map;
  }, [suppliers.data]);

  const [modal, setModal] = useState<{ rateCard: RateCard | null } | null>(null);

  const newButton = (
    <Button variant="primary" onClick={() => setModal({ rateCard: null })}>
      New rate card
    </Button>
  );

  return (
    <>
      <Panel>
        <PanelHead
          title="Rate cards"
          sub={
            rateCards.data
              ? `${rateCards.data.total} terms code${rateCards.data.total === 1 ? "" : "s"}`
              : "supplier terms codes, e.g. REIN01/414"
          }
          actions={
            <>
              <select
                className="field"
                style={{ width: 220 }}
                value={supplierFilter}
                onChange={(event) => setSupplierFilter(event.target.value)}
                aria-label="Filter by supplier"
              >
                <option value="">All suppliers</option>
                {(suppliers.data?.items ?? []).map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
              {newButton}
            </>
          }
        />
        {rateCards.isPending ? (
          <PanelBody>
            <EmptyState title="Loading rate cards…" />
          </PanelBody>
        ) : rateCards.isError ? (
          <PanelBody>
            <EmptyState title="Could not load rate cards" hint={errorMessage(rateCards.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No rate cards"
              hint="e.g. UKPS main REIN01/414, developer cards REI01A–D, F /414."
              action={newButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Terms code</th>
                <th>Name</th>
                <th>Kind</th>
                <th>Supplier</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((rateCard) => {
                const supplier = supplierById.get(rateCard.supplierId);
                const kind = KIND_LABEL[rateCard.kind];
                return (
                  <tr
                    key={rateCard.id}
                    onClick={() => setModal({ rateCard })}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="mono">
                      <strong>{rateCard.termsCode}</strong>
                    </td>
                    <td>{rateCard.name}</td>
                    <td>
                      <Pill tone={kind.tone}>{kind.label}</Pill>
                    </td>
                    <td>
                      {supplier ? (
                        <span className="result-cell">
                          <SupplierAvatar name={supplier.name} logo={supplier.logo} />
                          {supplier.name}
                        </span>
                      ) : (
                        <span className="mono">{rateCard.supplierId.slice(0, 8)}</span>
                      )}
                    </td>
                    <td>
                      <Button
                        mini
                        onClick={(event) => {
                          event.stopPropagation();
                          setModal({ rateCard });
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Panel>
      {modal && (
        <RateCardModal
          key={modal.rateCard?.id ?? "new"}
          rateCard={modal.rateCard}
          suppliers={suppliers.data?.items ?? []}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Create/edit modal
// ---------------------------------------------------------------------------

function RateCardModal({
  rateCard,
  suppliers,
  onClose,
}: {
  rateCard: RateCard | null;
  suppliers: Supplier[];
  onClose: () => void;
}) {
  const toast = useToast();
  const create = useCreateRateCard();
  const update = useUpdateRateCard();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof RateCardInput>, unknown, z.output<typeof RateCardInput>>({
    resolver: zodResolver(RateCardInput),
    defaultValues: {
      supplierId: rateCard?.supplierId ?? "",
      termsCode: rateCard?.termsCode ?? "",
      name: rateCard?.name ?? "",
      kind: rateCard?.kind ?? "main",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (rateCard) {
        await update.mutateAsync({ id: rateCard.id, input: values });
        toast.push({ message: "Rate card updated", tone: "ok" });
      } else {
        await create.mutateAsync(values);
        toast.push({ message: "Rate card created", tone: "ok" });
      }
      onClose();
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title={rateCard ? "Edit rate card" : "New rate card"}
      sub={rateCard ? `${rateCard.termsCode} — ${rateCard.name}` : "a supplier terms code"}
      onClose={onClose}
      footer={
        <>
          <span className="sub">Prices are keyed by product × rate card.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="rate-card-form" disabled={isSubmitting}>
              {rateCard ? "Save changes" : "Create rate card"}
            </Button>
          </div>
        </>
      }
    >
      <form id="rate-card-form" className="stack" onSubmit={onSubmit} noValidate>
        <FieldGrid>
          <Field
            label="Supplier"
            error={errors.supplierId ? "Choose a supplier" : undefined}
          >
            <select className="field" {...register("supplierId")}>
              <option value="">Choose…</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Kind" error={errors.kind?.message}>
            <select className="field" {...register("kind")}>
              <option value="main">Main account terms</option>
              <option value="customer_developer">Customer / developer terms</option>
              <option value="supplier_brand">Supplier brand card</option>
            </select>
          </Field>
          <Field label="Terms code" hint="e.g. REIN01/414" error={errors.termsCode?.message}>
            <input className="field" {...register("termsCode")} />
          </Field>
          <Field label="Name" hint="e.g. MAIN ACCOUNT TERMS" error={errors.name?.message}>
            <input className="field" {...register("name")} />
          </Field>
        </FieldGrid>
      </form>
    </Modal>
  );
}
